import { useEffect, useRef, useState } from 'react';
import {
  useAccount,
  usePublicClient,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { TransactionNotFoundError, type Address, type TransactionReceipt } from 'viem';
import { dcaManagerAbi } from '@/shared/lib/abi/dcaManager';
import { CONTRACT_ADDRESS } from '@/shared/lib/chains';
import {
  adoptScheduleActionReplacement,
  claimDepositFollowUp,
  clearScheduleAction,
  clearScheduleActionForHash,
  confirmScheduleAction,
  getPendingScheduleAction,
  hydrateScheduleActionScope,
  isScheduleActionStorageKey,
  markDepositFollowUpUncertain,
  markScheduleActionSubmitted,
  pruneExpiredScheduleFieldOverrides,
  pruneStaleSubmittingActions,
  readPersistedPendingScheduleAction,
  ROOTSTOCK_FINALITY_STABILITY_MS,
  setReconciledScheduleOrder,
  setReconciledScheduleValue,
  syncScheduleActionScopeFromStorage,
  syncPendingScheduleActionFromStorage,
  usePendingScheduleActions,
  withScheduleSubmissionLock,
  type OverrideField,
  type PendingScheduleAction,
} from '../lib/scheduleActionStore';
import {
  invalidateScheduleData,
  scheduleReconciliationRefetches,
} from '../lib/scheduleQueryInvalidation';

const SUBMITTING_PRUNE_INTERVAL_MS = 60 * 1000;
const RECEIPT_RECOVERY_INTERVAL_MS = 30 * 1000;
/** Only abandon receipt tracking when repeated RPC answers prove the hash is absent. */
const RECEIPT_ABANDON_MS = 2 * 60 * 60 * 1000;
const RECEIPT_ABANDON_NOT_FOUND_CONFIRMATIONS = 3;
const UNCERTAIN_DEPOSIT_RETRY_INTERVAL_MS = 30 * 1000;
const RECONCILIATION_RETRY_DELAYS_MS = [0, 2_000, 5_000, 15_000] as const;
const RECONCILIATION_MAX_DELAY_MS = 30_000;
const RECONCILIATION_WARN_AFTER_ATTEMPTS = 10;

type ChainSchedule = Record<string | number, unknown>;

/** Confirmation feedback per action; delete's message also warns about API lag. */
const SUCCESS_TOAST_KEYS: Record<PendingScheduleAction['action'], string> = {
  withdraw: 'schedules:withdraw.success',
  deposit: 'schedules:deposit.success',
  edit: 'schedules:edit.success',
  delete: 'schedules:edit.deleteSuccessPending',
};

/** Prevents duplicate deposit prompts across StrictMode remounts for one approval hash. */
const depositFollowUpInflight = new Set<string>();

function sleep(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, delayMs);
    signal.addEventListener('abort', () => {
      clearTimeout(timeout);
      resolve();
    }, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function depositFollowUpKey(entry: PendingScheduleAction): string {
  return `${entry.chainId}:${entry.account}:${entry.scheduleId}:${entry.hash ?? ''}`;
}

async function withDepositFollowUpLock<T>(
  lockManager: LockManager,
  key: string,
  callback: () => Promise<T>,
): Promise<T> {
  return lockManager.request(`bitchill:deposit-follow-up:${key}`, { mode: 'exclusive' }, callback);
}

function findSchedule(result: readonly unknown[], scheduleId: string): ChainSchedule | undefined {
  const normalizedId = scheduleId.toLowerCase();
  return result.find((candidate): candidate is ChainSchedule => {
    if (!candidate || typeof candidate !== 'object') return false;
    const schedule = candidate as ChainSchedule;
    const id = schedule.scheduleId ?? schedule[4];
    return typeof id === 'string' && id.toLowerCase() === normalizedId;
  });
}

function getFieldValue(schedule: ChainSchedule, kind: OverrideField): string {
  const index = kind === 'balance' ? 0 : kind === 'purchaseAmount' ? 1 : 2;
  const namedField = kind === 'balance' ? 'tokenBalance' : kind;
  const value = schedule[namedField] ?? schedule[index];
  if (typeof value !== 'bigint') throw new Error(`Invalid ${kind} in contract response`);
  return value.toString();
}

function getScheduleIds(result: readonly unknown[]): string[] {
  return result.map((candidate) => {
    if (!candidate || typeof candidate !== 'object') {
      throw new Error('Invalid schedule in contract response');
    }
    const schedule = candidate as ChainSchedule;
    const scheduleId = schedule.scheduleId ?? schedule[4];
    if (typeof scheduleId !== 'string') {
      throw new Error('Invalid schedule id in contract response');
    }
    return scheduleId.toLowerCase();
  });
}

function isExplicitUserRejection(error: unknown): boolean {
  const visited = new Set<unknown>();
  let current = error;
  while (current && typeof current === 'object' && !visited.has(current)) {
    visited.add(current);
    const record = current as Record<string, unknown>;
    if (
      record.code === 4001 ||
      record.code === 'ACTION_REJECTED' ||
      record.name === 'UserRejectedRequestError'
    ) {
      return true;
    }
    const text = [record.message, record.shortMessage, record.details]
      .filter((value): value is string => typeof value === 'string')
      .join(' ')
      .toLowerCase();
    if (
      text.includes('user rejected') ||
      text.includes('user denied') ||
      text.includes('rejected the request')
    ) {
      return true;
    }
    current = record.cause;
  }
  return false;
}

async function sendDepositFollowUp(
  entry: PendingScheduleAction,
  writeContractAsync: ReturnType<typeof useWriteContract>['writeContractAsync'],
  queryClient: ReturnType<typeof useQueryClient>,
  t: (key: string) => string,
): Promise<void> {
  const inflightKey = depositFollowUpKey(entry);
  if (depositFollowUpInflight.has(inflightKey)) return;
  depositFollowUpInflight.add(inflightKey);

  const lockManager = Reflect.get(navigator, 'locks') as LockManager | undefined;
  if (!lockManager) {
    // localStorage read/write is not an atomic cross-tab claim. Fall back to a
    // manual retry, which sends depositToken directly because approval mined.
    clearScheduleAction(entry.chainId, entry.account, entry.scheduleId);
    invalidateScheduleData(queryClient);
    toast.error(t('tx.approvalReadyRetry'));
    depositFollowUpInflight.delete(inflightKey);
    return;
  }

  try {
    await withDepositFollowUpLock(lockManager, inflightKey, async () => {
      await withScheduleSubmissionLock(entry.chainId, entry.account, async () => {
        // The Web Locks serialize tabs; storage is the shared source of truth
        // after acquiring them. Another tab may already have advanced to the
        // deposit hash while this tab was waiting.
        const persisted = readPersistedPendingScheduleAction(
          entry.chainId,
          entry.account,
          entry.scheduleId,
        );
        const stillApproval =
          persisted?.action === 'deposit' &&
          persisted.transactionPhase === 'approval' &&
          persisted.hash === entry.hash;
        if (!stillApproval) {
          syncPendingScheduleActionFromStorage(entry.chainId, entry.account, entry.scheduleId);
          return;
        }
        syncPendingScheduleActionFromStorage(entry.chainId, entry.account, entry.scheduleId);
        if (!claimDepositFollowUp(entry.chainId, entry.account, entry.scheduleId)) return;
        if (!entry.depositAmount) {
          clearScheduleAction(entry.chainId, entry.account, entry.scheduleId);
          invalidateScheduleData(queryClient);
          toast.error(t('tx.approvalReadyRetry'));
          return;
        }
        try {
          const stillPending = getPendingScheduleAction(entry.chainId, entry.account, entry.scheduleId);
          if (!stillPending || stillPending.transactionPhase !== 'approval') return;

          const hash = await writeContractAsync({
            account: entry.account as Address,
            chainId: entry.chainId,
            address: CONTRACT_ADDRESS,
            abi: dcaManagerAbi,
            functionName: 'depositToken',
            args: [
              entry.token as Address,
              BigInt(entry.scheduleIndex),
              entry.scheduleId as `0x${string}`,
              BigInt(entry.depositAmount),
            ],
          });
          const tracked = markScheduleActionSubmitted(
            entry.chainId,
            entry.account,
            entry.scheduleId,
            hash,
            'action',
          );
          if (!tracked) throw new Error(`Submitted deposit ${hash} lost its action reservation`);
        } catch (error) {
          if (isExplicitUserRejection(error)) {
            clearScheduleAction(entry.chainId, entry.account, entry.scheduleId);
            invalidateScheduleData(queryClient);
            toast.error(t('tx.userRejected'));
            return;
          }
          // A transport/provider failure can happen after broadcast. Preserve
          // the shared reservation instead of enabling an ambiguous duplicate.
          markDepositFollowUpUncertain(entry.chainId, entry.account, entry.scheduleId);
          toast.error(t('tx.depositSubmissionUncertain'));
        }
      });
    });
  } catch {
    // Lock/storage coordination failed before this tab owned the follow-up.
    // Keep shared state intact because another tab may already have advanced it.
    toast.error(t('tx.trackingLost'));
  } finally {
    depositFollowUpInflight.delete(inflightKey);
  }
}

/** Route-independent tracking for schedule transactions and staged deposits. */
export function ScheduleActionWatchers() {
  const { address, chainId } = useAccount();

  useEffect(() => {
    if (!address || chainId === undefined) return;
    hydrateScheduleActionScope(chainId, address);
  }, [address, chainId]);

  useEffect(() => {
    if (!address || chainId === undefined) return;
    const syncFromOtherTab = (event: StorageEvent) => {
      if (!isScheduleActionStorageKey(event.key, chainId, address)) return;
      syncScheduleActionScopeFromStorage(chainId, address);
    };
    window.addEventListener('storage', syncFromOtherTab);
    return () => {
      window.removeEventListener('storage', syncFromOtherTab);
    };
  }, [address, chainId]);

  useEffect(() => {
    const prune = () => {
      pruneStaleSubmittingActions();
      if (address && chainId !== undefined) {
        pruneExpiredScheduleFieldOverrides(chainId, address);
      }
    };
    prune();
    const interval = setInterval(prune, SUBMITTING_PRUNE_INTERVAL_MS);
    return () => {
      clearInterval(interval);
    };
  }, [address, chainId]);

  const trackedActions = usePendingScheduleActions().filter((entry) => entry.hash !== undefined);

  return (
    <>
      {trackedActions.map((entry) => (
        <ScheduleActionReceiptWatcher key={entry.hash} entry={entry} />
      ))}
    </>
  );
}

function ScheduleActionReceiptWatcher({ entry }: { entry: PendingScheduleAction }) {
  const { t } = useTranslation(['common', 'schedules']);
  const queryClient = useQueryClient();
  const publicClient = usePublicClient({ chainId: entry.chainId });
  const { writeContractAsync } = useWriteContract();
  const handledRef = useRef(false);
  const trackingErrorShownRef = useRef(false);
  const reconciliationWarnShownRef = useRef(false);
  const consecutiveNotFoundRef = useRef(0);
  const ignoredReplacementReceiptRef = useRef(false);
  const [recoveredReceipt, setRecoveredReceipt] = useState<TransactionReceipt>();

  // Replacement callbacks handle normal cancellation and speed-up flows. The
  // recovery loop below handles hashes that were never propagated.
  const { data: receipt, isError } = useWaitForTransactionReceipt({
    hash: entry.hash,
    onReplaced: ({ reason, replacedTransaction, transaction }) => {
      ignoredReplacementReceiptRef.current = true;
      if (reason === 'repriced') {
        adoptScheduleActionReplacement(
          entry.chainId,
          entry.account,
          entry.scheduleId,
          replacedTransaction.hash,
          transaction.hash,
        );
        return;
      }

      const cleared = clearScheduleActionForHash(
        entry.chainId,
        entry.account,
        entry.scheduleId,
        replacedTransaction.hash,
      );
      if (!cleared) return;
      invalidateScheduleData(queryClient);
      toast.error(t(reason === 'cancelled' ? 'tx.cancelled' : 'tx.replaced'));
    },
  });
  const effectiveReceipt = ignoredReplacementReceiptRef.current
    ? undefined
    : receipt ?? recoveredReceipt;

  // If wagmi's query exhausts its retries, retain the lock and independently
  // look up the hash until the RPC can answer again.
  useEffect(() => {
    if (!entry.hash || !publicClient || effectiveReceipt) return;
    const hash = entry.hash;
    const controller = new AbortController();

    if (isError && !trackingErrorShownRef.current) {
      trackingErrorShownRef.current = true;
      toast.error(t('tx.trackingLost'));
    }

    void (async () => {
      while (!controller.signal.aborted) {
        if (Date.now() - entry.submittedAt >= RECEIPT_ABANDON_MS) {
          try {
            await publicClient.getTransaction({ hash });
            controller.signal.throwIfAborted();
            consecutiveNotFoundRef.current = 0;
          } catch (error) {
            if (isAbortError(error)) return;
            consecutiveNotFoundRef.current = error instanceof TransactionNotFoundError
              ? consecutiveNotFoundRef.current + 1
              : 0;
          }
          if (
            consecutiveNotFoundRef.current >= RECEIPT_ABANDON_NOT_FOUND_CONFIRMATIONS
          ) {
            const cleared = clearScheduleActionForHash(
              entry.chainId,
              entry.account,
              entry.scheduleId,
              hash,
            );
            if (cleared) {
              invalidateScheduleData(queryClient);
              toast.error(t('tx.abandoned'));
            }
            return;
          }
        }

        if (!isError) {
          await sleep(RECEIPT_RECOVERY_INTERVAL_MS, controller.signal);
          continue;
        }

        try {
          const recovered = await publicClient.getTransactionReceipt({ hash });
          controller.signal.throwIfAborted();
          setRecoveredReceipt(recovered);
          return;
        } catch {
          await sleep(RECEIPT_RECOVERY_INTERVAL_MS, controller.signal);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [isError, entry.hash, entry.submittedAt, entry.chainId, entry.account, entry.scheduleId, publicClient, effectiveReceipt, queryClient, t]);

  useEffect(() => {
    if (!effectiveReceipt || !publicClient) return;

    if (effectiveReceipt.status !== 'success') {
      if (handledRef.current) return;
      handledRef.current = true;
      clearScheduleAction(entry.chainId, entry.account, entry.scheduleId);
      invalidateScheduleData(queryClient);
      toast.error(t('tx.reverted'));
      return;
    }

    if (entry.status === 'reconciling') return;

    if (entry.transactionPhase === 'approval') {
      if (entry.status === 'uncertain') return;
      void sendDepositFollowUp(entry, writeContractAsync, queryClient, t);
      return;
    }

    if (handledRef.current) return;
    handledRef.current = true;

    // Install the predicted override immediately, but retain the lock until a
    // direct read from the receipt block establishes the authoritative value.
    confirmScheduleAction(entry.chainId, entry.account, entry.scheduleId);
    invalidateScheduleData(queryClient);
    // Guarded by handledRef and the reconciling early-return, so this fires
    // once per receipt even across StrictMode remounts and refreshes.
    toast.success(t(SUCCESS_TOAST_KEYS[entry.action]));
    // entry is decomposed above; the full object would retrigger on every store tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    effectiveReceipt,
    entry.chainId,
    entry.account,
    entry.scheduleId,
    entry.token,
    entry.scheduleIndex,
    entry.action,
    entry.status,
    entry.transactionPhase,
    entry.depositAmount,
    publicClient,
    queryClient,
    t,
    writeContractAsync,
  ]);

  useEffect(() => {
    if (
      entry.status !== 'uncertain' ||
      entry.transactionPhase !== 'approval' ||
      !entry.hash ||
      !publicClient
    ) {
      return;
    }
    const trackedHash = entry.hash;
    const controller = new AbortController();
    const signal = controller.signal;

    void (async () => {
      while (!signal.aborted) {
        try {
          const result = await publicClient.readContract({
            address: CONTRACT_ADDRESS,
            abi: dcaManagerAbi,
            functionName: 'getMyDcaSchedules',
            args: [entry.token as Address],
            account: entry.account as Address,
          });
          signal.throwIfAborted();
          const schedule = findSchedule(result, entry.scheduleId);
          if (schedule && entry.override?.kind === 'balance') {
            const observedBalance = BigInt(getFieldValue(schedule, 'balance'));
            if (observedBalance >= BigInt(entry.override.expected)) {
              const cleared = clearScheduleActionForHash(
                entry.chainId,
                entry.account,
                entry.scheduleId,
                trackedHash,
              );
              if (cleared) {
                invalidateScheduleData(queryClient);
                toast.success(t(SUCCESS_TOAST_KEYS.deposit));
              }
              return;
            }
          }
        } catch (error) {
          if (isAbortError(error)) return;
        }

        if (Date.now() - entry.submittedAt >= ROOTSTOCK_FINALITY_STABILITY_MS) {
          const cleared = clearScheduleActionForHash(
            entry.chainId,
            entry.account,
            entry.scheduleId,
            trackedHash,
          );
          if (cleared) {
            invalidateScheduleData(queryClient);
            toast.error(t('tx.depositSubmissionUnresolved'));
          }
          return;
        }
        await sleep(UNCERTAIN_DEPOSIT_RETRY_INTERVAL_MS, signal);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    entry.account,
    entry.chainId,
    entry.hash,
    entry.override,
    entry.scheduleId,
    entry.status,
    entry.submittedAt,
    entry.token,
    entry.transactionPhase,
    publicClient,
    queryClient,
    t,
  ]);

  useEffect(() => {
    if (
      !effectiveReceipt ||
      effectiveReceipt.status !== 'success' ||
      entry.transactionPhase !== 'action' ||
      entry.status !== 'reconciling' ||
      !publicClient
    ) {
      return;
    }
    const reconciliationController = new AbortController();
    const reconciliationSignal = reconciliationController.signal;
    const confirmedAt = entry.confirmedAt ?? Date.now();
    void (async () => {
      let attempt = 0;
      while (!reconciliationSignal.aborted) {
        try {
          const delayMs = RECONCILIATION_RETRY_DELAYS_MS[attempt] ?? RECONCILIATION_MAX_DELAY_MS;
          if (delayMs > 0) await sleep(delayMs, reconciliationSignal);
          reconciliationSignal.throwIfAborted();
          const mayUseLatest =
            Date.now() - confirmedAt >= ROOTSTOCK_FINALITY_STABILITY_MS;
          const result = await publicClient.readContract({
            address: CONTRACT_ADDRESS,
            abi: dcaManagerAbi,
            functionName: 'getMyDcaSchedules',
            args: [entry.token as Address],
            account: entry.account as Address,
            ...(mayUseLatest ? {} : { blockNumber: effectiveReceipt.blockNumber }),
          });
          const schedule = findSchedule(result, entry.scheduleId);

          if (entry.action === 'delete') {
            if (schedule) throw new Error('Deleted schedule is still present at receipt block');
            setReconciledScheduleOrder(
              entry.chainId,
              entry.account,
              entry.token,
              getScheduleIds(result),
              confirmedAt,
            );
          } else {
            if (!schedule) throw new Error('Schedule missing after confirmed transaction');
            if (entry.override && entry.override.kind !== 'delete') {
              setReconciledScheduleValue(
                entry.chainId,
                entry.account,
                entry.scheduleId,
                entry.override.kind,
                getFieldValue(schedule, entry.override.kind),
              );
            }
          }

          invalidateScheduleData(queryClient);
          clearScheduleAction(entry.chainId, entry.account, entry.scheduleId);
          if (entry.hash) scheduleReconciliationRefetches(queryClient, entry.hash);
          return;
        } catch {
          attempt += 1;
          if (
            attempt === RECONCILIATION_WARN_AFTER_ATTEMPTS &&
            !reconciliationWarnShownRef.current
          ) {
            reconciliationWarnShownRef.current = true;
            toast.error(t('tx.reconciliationDelayed'));
          }
        }
      }
    })();

    return () => {
      reconciliationController.abort();
    };
  }, [
    effectiveReceipt,
    entry.chainId,
    entry.account,
    entry.scheduleId,
    entry.token,
    entry.scheduleIndex,
    entry.action,
    entry.status,
    entry.confirmedAt,
    entry.transactionPhase,
    entry.hash,
    entry.override,
    publicClient,
    queryClient,
    t,
  ]);

  return null;
}
